import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ReportShell } from "@/features/reports/ReportShell";
import { useBranch } from "@/context/BranchContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isBranchLocked } from "@/lib/roles";
import { Loader2 } from "lucide-react";
import type { ReportFilters } from "@/features/reports/types";
import { useWoSpareConsumptionReport } from "@/features/spares/reportsApi";

export const Route = createFileRoute("/_authenticated/reports/spare-consumption")({
  component: SpareConsumptionReport,
});

const WO_TYPES = ["Breakdown", "PM", "Calibration"];
const RETURN_STATUSES = ["Not Returned", "Partially Returned", "Fully Returned"];

function SpareConsumptionReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const [woType, setWoType] = useState<string>("all");
  const [returnStatus, setReturnStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const { currentBranchId } = useBranch();
  const { data: me } = useCurrentUser();
  const lockedBranchId = isBranchLocked(me?.roles) ? (me?.profile?.branch_id ?? null) : null;
  const effectiveBranchId = lockedBranchId ?? filters.branchId ?? currentBranchId ?? null;

  const { data, isLoading } = useWoSpareConsumptionReport({
    branchId: effectiveBranchId,
    departmentId: filters.departmentId ?? null,
    categoryId: filters.categoryId ?? null,
    dateFrom: filters.dateFrom ?? null,
    dateTo: filters.dateTo ?? null,
    woType: woType === "all" ? null : woType,
    returnStatus: returnStatus === "all" ? null : returnStatus,
    search: search || null,
  });
  const rows = data ?? [];

  const exportRows = useMemo(() => () => rows.map((r) => {
    const net = Number(r.quantity ?? 0) - Number(r.returned_quantity ?? 0);
    const rate = Number(r.unit_cost ?? 0);
    return {
      "WO No": r.work_order?.wo_number ?? "",
      "WO Type": r.work_order?.type ?? "",
      "Asset Code": r.work_order?.asset?.asset_code ?? "",
      "Asset Name": r.work_order?.asset?.name ?? "",
      Branch: r.branch?.name ?? "",
      Department: r.department?.name ?? "",
      "Spare Code": r.spare?.code ?? "",
      "Spare Name": r.spare?.name ?? r.spare_name,
      "Issued Qty": r.quantity,
      "Returned Qty": r.returned_quantity,
      "Net Used": net,
      "Unit Rate": rate,
      "Net Cost": +(net * rate).toFixed(2),
      "Issued By": r.issuer?.full_name ?? r.issuer?.email ?? "",
      "Issued At": r.created_at,
      "Return Status": r.return_status,
    };
  }), [rows]);

  return (
    <ReportShell
      id="R-38" title="Spare Consumption by Work Order"
      description="Spares issued and net used in Work Orders with returns applied."
      filters={filters} onChange={setFilters}
      enabled={["branchId", "departmentId", "categoryId", "dateFrom", "dateTo"]}
      exportRows={exportRows}
    >
      <Card>
        <CardContent className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <Label className="text-xs">WO Type</Label>
            <Select value={woType} onValueChange={setWoType}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {WO_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Return Status</Label>
            <Select value={returnStatus} onValueChange={setReturnStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {RETURN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">Search</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="WO / asset / spare" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>WO #</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Asset</TableHead>
                <TableHead>Branch / Dept</TableHead>
                <TableHead>Spare</TableHead>
                <TableHead className="text-right">Issued</TableHead>
                <TableHead className="text-right">Returned</TableHead>
                <TableHead className="text-right">Net Used</TableHead>
                <TableHead className="text-right">Unit Rate</TableHead>
                <TableHead className="text-right">Net Cost</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Issued By</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const net = Number(r.quantity ?? 0) - Number(r.returned_quantity ?? 0);
                  const rate = Number(r.unit_cost ?? 0);
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">{r.work_order?.wo_number}</TableCell>
                      <TableCell className="text-xs">{r.work_order?.type}</TableCell>
                      <TableCell className="text-xs"><div className="font-medium">{r.work_order?.asset?.name}</div><div className="font-mono text-muted-foreground">{r.work_order?.asset?.asset_code}</div></TableCell>
                      <TableCell className="text-xs">{r.branch?.name ?? "—"}<br />{r.department?.name ?? ""}</TableCell>
                      <TableCell className="text-xs"><div className="font-medium">{r.spare?.name ?? r.spare_name}</div><div className="font-mono text-muted-foreground">{r.spare?.code}</div></TableCell>
                      <TableCell className="text-right tabular-nums">{r.quantity}</TableCell>
                      <TableCell className="text-right tabular-nums">{r.returned_quantity}</TableCell>
                      <TableCell className="text-right tabular-nums">{net}</TableCell>
                      <TableCell className="text-right tabular-nums">{rate.toFixed(2)}</TableCell>
                      <TableCell className="text-right tabular-nums">{(net * rate).toFixed(2)}</TableCell>
                      <TableCell><Badge variant="secondary">{r.return_status}</Badge></TableCell>
                      <TableCell className="text-xs">{r.issuer?.full_name ?? r.issuer?.email ?? "—"}</TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={12} className="py-8 text-center text-sm text-muted-foreground">No spare records found for the selected filters.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
          <div className="border-t p-3 text-xs text-muted-foreground">Showing {rows.length} consumption rows.</div>
        </CardContent>
      </Card>
    </ReportShell>
  );
}
